/* eslint-disable global-require */

import { GetStaticProps, NextPage } from "next";
import NextImage from "next/image";
import { useRouter } from "next/router";

import { Button, Dialog, TextInput } from "@releaseit/ui";

import { Seo } from "@/components";
import { queryCMS } from "@/features/cms";

import SalesBannerADTImg from "@/assets/images/sales-banner-a-dt.webp";
import SalesBannerAMImg from "@/assets/images/sales-banner-a-m.webp";
import SalesBannerBDTImg from "@/assets/images/sales-banner-b-dt.webp";
import SalesBannerBMImg from "@/assets/images/sales-banner-b-m.webp";
import SalesClientTestimonialImg from "@/assets/images/sales-client-testimonial.webp";

const SITE_ID = "019ac3141b113eebf85a";
const FORM_ID = "01g296pm4sbsbddtn6x8d9pjxk";
const SUCCESS_URL = `https://releaseit.com.au/rental-businesses?success=true`;
const CIO_FORM_URL = `https://customerioforms.com/forms/submit_action?site_id=${SITE_ID}&form_id=${FORM_ID}&success_url=${SUCCESS_URL}`;

const WHY_LIST = [
  {
    heading: "Qualified Customers",
    icon: require("@/assets/icons/user-verified.svg").default,
    sub: "Releaseit users are in-market and ready to convert directly on your website. Our model is success-based, meaning you only pay for what we send.",
  },
  {
    heading: "Drive business growth",
    icon: require("@/assets/icons/dollar-filled.svg").default,
    sub: "Grow your business with a new customer base. With Releaseit, you own the entire transaction - we just hand the customer over to you.",
  },
  {
    heading: "Join the circular economy",
    icon: require("@/assets/icons/planet.svg").default,
    sub: "Join us on our journey to disrupt retail consumerism on behalf of the planet! Join Releaseit to gain access to our eco-conscious, younger demographic of users, who want to reduce waste by consuming less and renting more.",
  },
];

const PITCHES = [
  {
    heading: "Looking for new customers?",
    sub: "A lot of our partners have had a hard time finding customers recently, or just want more, and joined us to help expand and grow their business.",
  },
  {
    heading: "Looking to push further into digital advertising?",
    sub: "Some of our partners are inexperienced or relatively new to advertising online. We provide consultation on your online traffic, and how to maximise your results.",
  },
  {
    heading: "Other channels not working as they should?",
    sub: "Other partners of ours find that their other advertising channels don't work as they would like and are looking for a more direct way to get a hold of rental customers.",
  },
];

const SALES_INBOUND_QUERY = `
  query ClientLogos {
    featuredInLogos: featureIn {
      Logos {
        Label
        ExternalUrl
        Image {
          url
          width
          height
        }
      }
    }
    clientLogos: clientLogo {
      Logos {
        Label
        ExternalUrl
        Image {
          url
          width
          height
        }
      }
    }
  }
`;

type Props = {
  featuredInLogos: any;
  clientLogos: any;
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const { featuredInLogos, clientLogos } = await queryCMS(SALES_INBOUND_QUERY);

  return {
    props: { featuredInLogos, clientLogos },
  };
};

const RentalBusinessesPage: NextPage<Props> = ({ featuredInLogos, clientLogos }) => {
  const router = useRouter();

  return (
    <>
      <Seo
        templateTitle="Releaseit for Rental Businesses"
        description="The ultimate rental marketplace"
      />

      {/* Success Dialog */}
      {router.isReady && (
        <Dialog defaultOpen={router.query.success === "true"}>
          <Dialog.Content>
            <Dialog.Title>Success example title</Dialog.Title>
            <Dialog.Description className="mt-2">
              You&apos;ll be hearing from us soon! more text here...
            </Dialog.Description>
            <Dialog.Actions>
              <Button variant="light-positive">Awesome</Button>
            </Dialog.Actions>
          </Dialog.Content>
        </Dialog>
      )}

      <div className="pb-16">
        {/* Hero image */}
        <div className="md:hidden">
          <NextImage
            src={SalesBannerAMImg}
            alt="Rental businesses sales banner"
            layout="responsive"
            unoptimized
            placeholder="blur"
            priority
          />
        </div>
        <div className="hidden justify-center md:flex">
          <NextImage
            src={SalesBannerADTImg}
            alt="Rental businesses sales banner"
            unoptimized
            placeholder="blur"
            priority
          />
        </div>

        {/* Content */}
        <div className="container mt-16 space-y-12">
          {/* Featured In */}
          <section>
            <h2 className="text-center text-2xl font-medium">As featured in...</h2>
            <div className="mx-auto mt-4 flex flex-wrap justify-center gap-2">
              {featuredInLogos.Logos.map((logo: any) => (
                <a
                  key={logo.Label}
                  href={logo.ExternalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <NextImage
                    src={logo.Image.url}
                    width={(logo.Image.width / logo.Image.height) * 80}
                    height={80}
                    objectFit="contain"
                  />
                </a>
              ))}
            </div>
          </section>

          {/* Form */}
          <section className="flex flex-wrap gap-8 rounded-lg bg-off-white p-8 lg:flex-nowrap lg:px-14 lg:py-10">
            <div className="flex w-full flex-col justify-center lg:w-3/5">
              <h2 className="text-lg font-medium lg:text-2xl">
                We are building the number one online destination for rental discovery & inspiration
              </h2>

              <p className="mt-2 text-sm text-gray-800 lg:mt-4 lg:text-base">
                We aim to connect Australia&apos;s rental businesses with qualified, rental seekers.
                Request a call with someone from the team to learn more about Releaseit and the
                benefits of becoming one of our foundation partners.
              </p>
            </div>

            <div id="request-form" className="w-full scroll-m-8 lg:w-2/5">
              <h2 className="font-medium lg:text-lg">
                Request a call & we will be in touch shortly
              </h2>

              <form method="post" action={CIO_FORM_URL} className="mt-4 space-y-4">
                <input type="hidden" name="source" value="inbound sales" />
                <input type="hidden" name="account_type" value="b2c" />

                <TextInput name="name" type="text" label="Name" isRequired />
                <TextInput name="email" type="email" label="Email" isRequired />
                <TextInput name="company" type="text" label="Company name" isRequired />
                <TextInput name="phone" type="tel" label="Phone number" isRequired />

                <Button type="submit" className="w-full lg:w-auto">
                  Request a Call
                </Button>
              </form>
            </div>
          </section>

          {/* Why */}
          <section>
            <h2 className="mb-8 text-center text-lg font-medium lg:text-xl">
              Why list your products on Releaseit?
            </h2>

            <div className="grid grid-cols-1 gap-y-8 gap-x-16 md:grid-cols-3">
              {WHY_LIST.map(({ heading, sub, icon: Icon }) => (
                <div key={heading} className="flex flex-col items-center gap-y-2 px-2 text-center">
                  <Icon className="h-12 w-12 md:h-16 md:w-16" />
                  <p className="font-medium text-black md:text-lg">{heading}</p>
                  <p className="text-sm text-gray-800 md:text-base">{sub}</p>
                </div>
              ))}
            </div>
          </section>

          <div>
            <NextImage
              src={SalesClientTestimonialImg}
              alt="Rental businesses get started banner"
              layout="responsive"
              unoptimized
              priority
              placeholder="blur"
              className="rounded-md"
            />
          </div>

          {/* Pitches */}
          <section>
            <div className="grid grid-cols-1 gap-y-4 md:gap-x-12 lg:grid-cols-3 xl:gap-x-16">
              {PITCHES.map(({ heading, sub }) => (
                <div
                  key={heading}
                  className="flex flex-col items-center justify-center space-y-4 rounded-lg bg-off-white p-4 text-center md:px-6 md:py-8 lg:px-12 lg:py-14"
                >
                  <p className="font-medium text-black lg:text-lg">{heading}</p>
                  <p className="text-sm text-gray-800 lg:text-base">{sub}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Client logos */}
          <section className="mt-4 mb-2 px-4 lg:mt-6">
            <h2 className="mx-auto text-center text-xl font-medium" style={{ maxWidth: "50ch" }}>
              Join over 100 other rental businesses on Releaseit & let our specialists help you grow
              your business...
            </h2>
            <div className="mx-auto mt-8 flex flex-wrap items-center justify-center gap-2">
              {clientLogos.Logos.map((logo: any) => (
                <a
                  key={logo.Label}
                  href={logo.ExternalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <NextImage
                    src={logo.Image.url}
                    width={(logo.Image.width / logo.Image.height) * 96}
                    height={96}
                    objectFit="contain"
                  />
                </a>
              ))}
            </div>
          </section>

          {/* Footer image */}
          <section>
            <a href="#request-form">
              <div className="md:hidden">
                <NextImage
                  src={SalesBannerBMImg}
                  alt="Rental businesses get started banner"
                  layout="responsive"
                  unoptimized
                  placeholder="blur"
                  className="rounded-md"
                />
              </div>
              <div className="hidden justify-center md:flex">
                <NextImage
                  src={SalesBannerBDTImg}
                  alt="Rental businesses get started banner"
                  unoptimized
                  placeholder="blur"
                  className="rounded-md"
                />
              </div>
            </a>
          </section>
        </div>
      </div>
    </>
  );
};

export default RentalBusinessesPage;
